import axios from 'axios';
import '../SearchForm/SearchForm.css';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../../i18n/i18n';
import { useConfig } from '../../../config';
import { toQuery } from '../../Routes';
import { AutoCompletingInput } from '../SearchForm/AutoCompletingInput';
import { Icon, search } from '../../Icon/Icon';
import { useSession } from '../../../state/session';
import { SpecialProductsSearchInput } from '../SearchForm/SpecialProductsSearchInput';
import { MetaContainer } from '../../../state/meta';
import { generateUUID } from '../../utils';
import { CategoryTreeContainer } from '../../../state/categories';
import { AutocompleteItem, SearchSuggestion } from '../SearchForm/SearchSuggestion';
import { useSearchAutocomplete } from '../SearchForm/useSearchAutocomplete';

export const SearchFormLoop54 = (): JSX.Element => {
	const { loop54Api } = useConfig();
	const t = useTranslation().search;
	const urls = useTranslation().routes;
	const { user } = useSession();
	const { searchProfileId, randomizeSearchProfileId } = MetaContainer.useContainer();
	const { getMainCategoryId, getCategoryById } = CategoryTreeContainer.useContainer();

	const loop54PostHeaders = {
		'Api-Version': 'V3',
		'User-id': randomizeSearchProfileId ? generateUUID() : searchProfileId,
	};

	const { query, suggestions, handleFocus, handleChange, handleKeyDown, handlePickSuggestion } =
		useSearchAutocomplete({
			searchUrl: urls.search,
			normalizeQuery: (q) => q.slice(0, 100),
			fetchAutocompleteItems: async (q) => {
				if (q.trim() === '') return [];

				const autocompletePayload = {
					query: q,
					queriesOptions: {
						take: 9,
						skip: 0,
					},
				};

				const { data } = await axios.post(loop54Api + 'autoComplete', autocompletePayload, {
					withCredentials: false,
					headers: loop54PostHeaders,
				});
				if (!data) return [];

				let nextSuggestions: AutocompleteItem[] = data.queries.items.map((i: { query: string }) => {
					return { term: i.query, facetCategory: '' };
				});
				let facetCategoryIds: number[] = [];
				if (data.scopedQuery) {
					facetCategoryIds = data.scopedQuery.scopes.filter((scopeId: string) => {
						if (scopeId !== '0') return Number(scopeId);
					});
				}

				const facetCategoryNames = facetCategoryIds.map((facetCategoryId: number) => {
					const mainCategoryId = getMainCategoryId(facetCategoryId);
					if (mainCategoryId !== 0) {
						const facetCategoryName = getCategoryById(facetCategoryId)?.name;
						return mainCategoryId + '_' + facetCategoryId + '_' + facetCategoryName;
					}
				});

				nextSuggestions = [
					...facetCategoryNames.map((facet) => ({
						term: data.scopedQuery.query,
						facetCategory: facet ?? '',
					})),
					...nextSuggestions,
				];

				return nextSuggestions;
			},
		});

	if (!searchProfileId) return <></>;

	return (
		<div className="search-form">
			{user?.specialsOnly ? (
				<SpecialProductsSearchInput />
			) : (
				<AutoCompletingInput
					type="search"
					autoComplete="off"
					aria-label={t.searchForProducts}
					placeholder={t.placeholder}
					onFocus={(e) => handleFocus(e.target.value)}
					onChange={(e) => handleChange(e.target.value)}
					onKeyDown={handleKeyDown}
					onPickSuggestion={handlePickSuggestion}
					value={query}
					suggestions={suggestions}
					Suggestion={SearchSuggestion}
				/>
			)}
			<Link className="search-icon" to={`${urls.search}?${toQuery({ q: query || undefined })}`}>
				<Icon shape={search} size={20} />
			</Link>
		</div>
	);
};
